import { useState, useEffect, useCallback } from 'react';
import { C, card, sectionTitle } from '../../styles';
import { useAuth } from '../../context/AuthContext';
import {
  getTeamUsers, createTeamUser, updateTeamUser,
  archiveTeamUser, unarchiveTeamUser, getTeamBranches,
} from '../../services/api';
import api from '../../services/api';

// ─── styles ───────────────────────────────────────────────────────────────────

const inp = {
  width:'100%', padding:'10px 12px',
  background:C.bgElevated, border:`1px solid ${C.border}`,
  borderRadius:'8px', fontSize:'13px', color:C.textPrimary,
  outline:'none', boxSizing:'border-box', marginBottom:'12px',
};

const lbl = {
  fontSize:'11px', fontWeight:'600', color:C.textMuted,
  textTransform:'uppercase', letterSpacing:'0.5px',
  marginBottom:'5px', display:'block',
};

const ROLE_COLORS = {
  boss:           C.accent,
  superadmin:     C.purple,
  area_manager:   C.blue,
  branch_manager: '#a78bfa',
  staff:          C.amber,
  customer:       '#22d3ee',
};

const ROLE_LABELS = {
  boss:           'Boss',
  superadmin:     'Superadmin',
  area_manager:   'Area Manager',
  branch_manager: 'Branch Manager',
  staff:          'Staff',
  customer:       'Customer',
};

const CREATABLE_ROLES_BOSS       = ['branch_manager', 'area_manager', 'staff'];
const CREATABLE_ROLES_SUPERADMIN = ['boss', 'area_manager', 'branch_manager', 'staff', 'customer', 'superadmin'];

// ─── Alert ────────────────────────────────────────────────────────────────────

const Alert = ({ msg, type }) => {
  if (!msg) return null;
  const color = type === 'error' ? C.danger : C.accent;
  return (
    <div style={{ padding:'10px 14px', borderRadius:'8px', background:`${color}18`, color, fontSize:'12px', marginBottom:'12px', border:`1px solid ${color}44` }}>
      {msg}
    </div>
  );
};

const RoleBadge = ({ role }) => (
  <span style={{
    padding:'3px 10px', borderRadius:'20px', fontSize:'10px', fontWeight:'700',
    background:`${ROLE_COLORS[role] || C.textMuted}22`,
    color: ROLE_COLORS[role] || C.textMuted,
    border:`1px solid ${ROLE_COLORS[role] || C.textMuted}44`,
  }}>
    {ROLE_LABELS[role] || role}
  </span>
);

// ─── BranchPicker ─────────────────────────────────────────────────────────────

const BranchPicker = ({ branches, selected, onChange }) => {
  const toggle = (code) =>
    onChange(selected.includes(code) ? selected.filter(b => b !== code) : [...selected, code]);

  if (!branches.length)
    return <p style={{ fontSize:'12px', color:C.textMuted, marginBottom:'12px' }}>No branches available</p>;

  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'12px' }}>
      {branches.map(b => {
        const sel = selected.includes(b.branch_code);
        return (
          <button key={b.branch_code} onClick={() => toggle(b.branch_code)}
            style={{ padding:'6px 12px', borderRadius:'20px', fontSize:'12px', cursor:'pointer',
              border:`1px solid ${sel ? C.accent : C.border}`,
              background: sel ? C.accentDim : 'transparent',
              color:       sel ? C.accent    : C.textSec,
              fontWeight:  sel ? '600' : '400' }}
          >
            {b.branch_name || b.branch_code}
          </button>
        );
      })}
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const TeamManagement = () => {
  const { user: currentUser } = useAuth();

  const [users, setUsers]         = useState([]);
  const [branches, setBranches]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showArchived, setAr]     = useState(false);
  const [view, setView]           = useState('list');
  const [selected, setSelected]   = useState(null);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [branchErr, setBranchErr] = useState('');

  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isBoss       = currentUser?.role === 'boss';
  const canAccess    = isBoss || isSuperAdmin;

  const creatableRoles = isSuperAdmin ? CREATABLE_ROLES_SUPERADMIN : CREATABLE_ROLES_BOSS;

  const blank = { full_name:'', username:'', password:'', staff_code:'', role:'branch_manager', branch_ids:[], is_active:true };
  const [form, setForm] = useState(blank);

  // ── load users (independently from branches so one failure ≠ blank screen)

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = showArchived ? { archived: 'true' } : {};
      const res = await getTeamUsers(params);
      setUsers(res.data.data || []);
    } catch(e) {
      const msg = e?.response?.data?.error || e?.message || 'Unknown error';
      console.error('[TeamManagement] users error:', e?.response?.status, msg);
      setError(`Failed to load users: ${msg}`);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  const loadBranches = useCallback(async () => {
    setBranchErr('');
    try {
      const res = await getTeamBranches();
      setBranches(res.data.data || []);
    } catch(e) {
      const msg = e?.response?.data?.error || e?.message || '';
      console.warn('[TeamManagement] branches warning:', msg);
      // Don't block the UI — just note it
      setBranchErr('Could not load branches. Branch assignment will be unavailable.');
      setBranches([]);
    }
  }, []);

  // Load both on mount / archive toggle, but independently
  useEffect(() => {
    if (!canAccess) return;
    loadUsers();
    loadBranches();
  }, [showArchived, canAccess]);

  // ── flash ─────────────────────────────────────────────────────────────────

  const flash = (msg, isErr = false) => {
    if (isErr) { setError(msg); setSuccess(''); }
    else       { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!form.full_name || !form.username || !form.password) {
      flash('Full name, username and password are required', true); return;
    }
    if (form.staff_code && !window.confirm(
      `Please confirm: Staff Code "${form.staff_code}" matches the HR Code in the system exactly (case-sensitive). Proceed?`
    )) return;
    try {
      await createTeamUser({
        full_name:  form.full_name,
        username:   form.username,
        password:   form.password,
        staff_code: form.staff_code,
        role:       form.role,
        branch_ids: form.branch_ids,
      });
      flash('User created successfully');
      setForm(blank);
      setView('list');
      loadUsers();
    } catch(e) { flash(e?.response?.data?.error || 'Failed to create user', true); }
  };

  const handleUpdate = async () => {
    try {
      if (form.staff_code && !window.confirm(
        `Please confirm: Staff Code "${form.staff_code}" matches the HR Code in the system exactly (case-sensitive). Proceed?`
      )) return;
      const payload = {
        full_name:  form.full_name,
        staff_code: form.staff_code || null,
        role:       form.role,
        branch_ids: form.branch_ids,
        is_active:  form.is_active,
      };
      if (form.password) payload.password = form.password;
      await updateTeamUser(selected.user_id, payload);
      flash('User updated successfully');
      setView('list');
      setSelected(null);
      loadUsers();
    } catch(e) { flash(e?.response?.data?.error || 'Failed to update user', true); }
  };

  const handleToggleActive = async (u) => {
    try {
      await updateTeamUser(u.user_id, { is_active: !u.is_active });
      flash(`User ${u.is_active ? 'deactivated' : 'activated'}`);
      loadUsers();
    } catch(e) { flash(e?.response?.data?.error || 'Failed to update status', true); }
  };

  const handleArchive = async (u) => {
    if (!window.confirm(`Archive ${u.full_name}? They must be deactivated first.`)) return;
    try {
      await archiveTeamUser(u.user_id);
      flash('User archived');
      loadUsers();
    } catch(e) { flash(e?.response?.data?.error || 'Failed to archive', true); }
  };

  const handleUnarchive = async (u) => {
    try {
      await unarchiveTeamUser(u.user_id);
      flash('User unarchived');
      loadUsers();
    } catch(e) { flash('Failed to unarchive', true); }
  };

  const openEdit = (u) => {
    setSelected(u);
    setForm({
      full_name:   u.full_name,
      username:    u.username,
      password:    '',
      role:        u.role,
      staff_code:  u.staff_code || '',
      branch_ids:  u.branch_ids || [],
      is_active:   u.is_active,
    });
    setView('edit');
  };

  const goBack = () => { setView('list'); setSelected(null); setError(''); setSuccess(''); };

  // ── access guard ──────────────────────────────────────────────────────────

  if (!canAccess) {
    return (
      <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'300px', textAlign:'center', padding:'20px' }}>
        <div>
          <div style={{ fontSize:'32px', marginBottom:'12px' }}>🔒</div>
          <p style={{ color:C.textMuted, fontSize:'13px' }}>Team Management is only available to Boss and Superadmin.</p>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ─────────────────────────────────────────────────────────────

  if (view === 'list') return (
    <div style={{ padding:'16px', paddingBottom:'80px' }}>
      <Alert msg={success} type="success" />
      <Alert msg={error}   type="error" />

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
        <div>
          <p style={{ fontSize:'17px', fontWeight:'700', color:C.textPrimary, margin:0 }}>
            {showArchived ? 'Archived Members' : 'Team Management'}
          </p>
          <p style={{ fontSize:'11px', color:C.textMuted, margin:'2px 0 0' }}>
            {loading ? '…' : `${users.length} ${showArchived ? 'archived' : 'active'} member${users.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={() => setAr(v => !v)}
            style={{ padding:'7px 12px', background:showArchived ? 'rgba(251,183,36,0.15)' : C.bgElevated, color:showArchived ? C.amber : C.textSec, border:`1px solid ${showArchived ? C.amber : C.border}`, borderRadius:'8px', fontSize:'12px', cursor:'pointer' }}>
            {showArchived ? '← Active' : '🗄 Archived'}
          </button>
          {!showArchived && (
            <button onClick={() => { setForm(blank); setView('create'); }}
              style={{ padding:'7px 14px', background:C.accent, color:'#000', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'700', cursor:'pointer' }}>
              + Add Member
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'40px 0' }}>
          <div style={{ width:'32px', height:'32px', border:`2px solid ${C.bgElevated}`, borderTopColor:C.accent, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
        </div>
      ) : users.length === 0 && !error ? (
        <div style={{ ...card, textAlign:'center', padding:'40px' }}>
          <div style={{ fontSize:'32px', marginBottom:'10px' }}>{showArchived ? '🗄' : '👥'}</div>
          <p style={{ color:C.textMuted, fontSize:'13px' }}>
            {showArchived ? 'No archived members' : 'No team members yet — add one above'}
          </p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {users.map(u => (
            <div key={u.user_id} style={{ ...card, marginBottom:0, display:'flex', justifyContent:'space-between', alignItems:'center', opacity: u.is_active ? 1 : 0.6 }}>
              {/* Left: info */}
              <div onClick={() => !showArchived && openEdit(u)}
                style={{ display:'flex', alignItems:'center', gap:'12px', flex:1, cursor: showArchived ? 'default' : 'pointer', minWidth:0 }}>
                <div style={{ width:'40px', height:'40px', borderRadius:'50%', flexShrink:0,
                  background:`${ROLE_COLORS[u.role] || C.textMuted}22`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'15px', fontWeight:'700', color: ROLE_COLORS[u.role] || C.textMuted }}>
                  {(u.full_name || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                    <p style={{ fontSize:'13px', fontWeight:'600', color: u.is_active ? C.textPrimary : C.textMuted, margin:0 }}>{u.full_name}</p>
                    <RoleBadge role={u.role} />
                    {!u.is_active && <span style={{ padding:'2px 7px', borderRadius:'12px', fontSize:'10px', background:'rgba(255,107,107,0.12)', color:C.danger, fontWeight:'600' }}>Inactive</span>}
                  </div>
                  <p style={{ fontSize:'11px', color:C.textMuted, margin:'2px 0 0' }}>@{u.username}</p>
                  <div style={{ display:'flex', gap:'5px', flexWrap:'wrap', marginTop:'4px' }}>
                    {u.staff_code && <span style={{ padding:'2px 7px', borderRadius:'10px', fontSize:'10px', background:C.bgElevated, color:C.textSec }}>🆔 {u.staff_code}</span>}
                    {(u.branch_ids || []).map(b => (
                      <span key={b} style={{ padding:'2px 7px', borderRadius:'10px', fontSize:'10px', background:C.bgElevated, color:C.textSec }}>{b}</span>
                    ))}
                  </div>
                </div>
              </div>
              {/* Right: actions */}
              <div style={{ display:'flex', gap:'5px', marginLeft:'8px', flexShrink:0, alignItems:'center' }}>
                {showArchived ? (
                  <button onClick={() => handleUnarchive(u)}
                    style={{ padding:'5px 10px', background:C.accentDim, color:C.accent, border:`1px solid ${C.accent}`, borderRadius:'6px', fontSize:'11px', cursor:'pointer' }}>
                    Unarchive
                  </button>
                ) : (
                  <>
                    <button onClick={() => handleToggleActive(u)}
                      style={{ padding:'4px 9px', border:'none', borderRadius:'6px', fontSize:'10px', cursor:'pointer', fontWeight:'600',
                        background: u.is_active ? 'rgba(255,107,107,0.12)' : 'rgba(168,224,99,0.12)',
                        color:      u.is_active ? C.danger                  : C.accent }}>
                      {u.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    {!u.is_active && (
                      <button onClick={() => handleArchive(u)}
                        style={{ padding:'4px 9px', background:'rgba(251,183,36,0.12)', color:C.amber, border:`1px solid ${C.amber}`, borderRadius:'6px', fontSize:'10px', cursor:'pointer' }}>
                        Archive
                      </button>
                    )}
                    <span style={{ color:C.textMuted, fontSize:'18px', cursor:'pointer', lineHeight:1, paddingLeft:'4px' }} onClick={() => openEdit(u)}>›</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );

  // ── CREATE / EDIT FORM ────────────────────────────────────────────────────

  const isEdit = view === 'edit';
  const isSelf = isEdit && selected?.user_id === currentUser?.user_id;

  return (
    <div style={{ padding:'16px', paddingBottom:'80px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px' }}>
        <button onClick={goBack}
          style={{ background:C.bgElevated, border:`1px solid ${C.border}`, color:C.accent, borderRadius:'8px', padding:'6px 12px', fontSize:'12px', cursor:'pointer' }}>
          ← Back
        </button>
        <p style={{ ...sectionTitle, marginBottom:0 }}>{isEdit ? 'Edit Member' : 'New Member'}</p>
      </div>

      <Alert msg={error}   type="error" />
      <Alert msg={success} type="success" />
      {branchErr && <Alert msg={branchErr} type="error" />}

      {isSelf && (
        <div style={{ padding:'10px 14px', borderRadius:'8px', background:'rgba(251,183,36,0.1)', color:C.amber, fontSize:'12px', marginBottom:'12px', border:`1px solid ${C.amber}` }}>
          You cannot deactivate your own account.
        </div>
      )}

      <div style={card}>
        <label style={lbl}>Full Name</label>
        <input style={inp} value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Jane Doe" />

        <label style={lbl}>Username</label>
        <input style={{ ...inp, opacity: isEdit ? 0.5 : 1 }} value={form.username} disabled={isEdit}
          onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="jane.doe" />

        <label style={lbl}>{isEdit ? 'New Password (leave blank to keep)' : 'Password'}</label>
        <input style={inp} type="password" value={form.password}
          onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
          placeholder={isEdit ? 'Leave blank to keep current' : 'Set password'} />

        <label style={lbl}>Staff Code (must match HR Code exactly)</label>
        <input style={inp} value={form.staff_code}
          onChange={e => setForm(f => ({ ...f, staff_code: e.target.value.trim() }))} placeholder="e.g. EV01" />
        {form.staff_code && (
          <p style={{ fontSize:'11px', color:'#fbb724', marginTop:'-8px', marginBottom:'12px', display:'flex', alignItems:'center', gap:'5px' }}>
            ⚠ Ensure this matches the HR Code exactly — it is case-sensitive.
          </p>
        )}

        <label style={lbl}>Role</label>
        <select style={inp} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
          {creatableRoles.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>

        <label style={lbl}>Branches (select multiple)</label>
        <BranchPicker branches={branches} selected={form.branch_ids} onChange={ids => setForm(f => ({ ...f, branch_ids: ids }))} />
        {form.branch_ids.length > 0 && (
          <p style={{ fontSize:'11px', color:C.textMuted, marginTop:'-8px', marginBottom:'12px' }}>
            Selected: {form.branch_ids.join(', ')}
          </p>
        )}



        {isEdit && !isSelf && (
          <>
            <label style={lbl}>Status</label>
            <div style={{ display:'flex', gap:'8px', marginBottom:'16px' }}>
              {[true, false].map(val => (
                <button key={String(val)} onClick={() => setForm(f => ({ ...f, is_active: val }))}
                  style={{ flex:1, padding:'8px', borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor:'pointer',
                    border:`1px solid ${form.is_active === val ? (val ? C.accent : C.danger) : C.border}`,
                    background: form.is_active === val ? (val ? C.accentDim : 'rgba(255,107,107,0.1)') : 'transparent',
                    color:      form.is_active === val ? (val ? C.accent    : C.danger)                : C.textMuted }}>
                  {val ? 'Active' : 'Inactive'}
                </button>
              ))}
            </div>
          </>
        )}

        <button onClick={isEdit ? handleUpdate : handleCreate}
          style={{ width:'100%', padding:'12px', background:C.accent, color:'#000', border:'none', borderRadius:'10px', fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
          {isEdit ? 'Save Changes' : 'Create Member'}
        </button>
      </div>

      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );
};

export default TeamManagement;